import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Offcanvas } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  getBlobData,
  getData,
  patchDataToken
} from "../../axios/axiosHelper";
import AccountStatusBadge from "./AccountStatusBadge";
import MerchantProfileCompleteness from "./MerchantProfileCompleteness";
import ProtectedProfileImage from "./ProtectedProfileImage";
import { protectedDocumentErrorMessage } from "./protectedDocumentError";
import RegistrationAccountOverview from "./RegistrationAccountOverview";
import RegistrationSubmissionHistory from "./RegistrationSubmissionHistory";
import SecureDocumentViewer from "./SecureDocumentViewer";
import useResizableDrawer from "./useResizableDrawer";
import UserManagementDateTime from "./UserManagementDateTime";

const messageFor = (error) => error?.response?.data?.message || "Action failed";

const statusLabel = (status, text) => ({
  ACTIVE: text.active,
  BLOCKED: text.blocked,
  DELETED: text.deleted,
  PENDING_REVIEW: text.pending,
  REUPLOAD_REQUIRED: text.reupload,
  REJECTED: text.rejected,
  APPROVED: text.approved
})[status] || status || "—";

const DetailGrid = ({ entries }) => (
  <dl className="account-detail-grid registration-review-detail-grid">
    {entries.map(([label, value]) => (
      <div key={label}><dt>{label}</dt><dd>{value || "—"}</dd></div>
    ))}
  </dl>
);

const SectionTitle = ({ icon, title, hint }) => (
  <div className="registration-review-section-title">
    <span><i className={`fa-solid ${icon}`} aria-hidden="true" /></span>
    <div><h3>{title}</h3>{hint && <p>{hint}</p>}</div>
  </div>
);

export const PolicyEvidenceList = ({ entries = [], text, language }) => (
  <div className="account-policy-evidence-grid">
    {entries.length === 0 ? <p className="account-drawer-empty">{text.evidenceMissing}</p> : entries.map((item) => (
      <article key={item.id}>
        <header>
          <span><i className="fa-solid fa-shield-halved" aria-hidden="true" /></span>
          <strong>{item.policyCode} · {item.version}</strong>
        </header>
        <dl>
          <div><dt>{text.policySource}</dt><dd>{item.source || "—"}</dd></div>
          <div><dt>{text.policyIp}</dt><dd>{item.ip || "—"}</dd></div>
          <div>
            <dt>{text.policyAcceptedAt}</dt>
            <dd><UserManagementDateTime value={item.acceptedAt} language={language} /></dd>
          </div>
        </dl>
      </article>
    ))}
  </div>
);

const auditActor = (item, text) =>
  text.auditActorTypes?.[item.actorType] || item.actorType || text.systemActor;

const auditChanges = (safeChanges, text) => {
  const fields = Object.keys(safeChanges || {});
  if (!fields.length) return "—";
  return fields.map((field) => text.auditFields?.[field] || field).join(", ");
};

export const AccountAuditLogTable = ({ entries = [], text, language }) => (
  <div className="account-audit-table-wrap">
    <table className="account-audit-table">
      <thead>
        <tr>
          <th>{text.logEvent}</th>
          <th>{text.logDate}</th>
          <th>{text.logActor}</th>
          <th>{text.reason}</th>
          <th>{text.logChanges}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((item) => (
          <tr key={item.id}>
            <td>
              <span className="account-audit-event">
                <i className="fa-solid fa-clock-rotate-left" aria-hidden="true" />
                {text.auditActions?.[item.action] || item.action}
              </span>
            </td>
            <td><UserManagementDateTime value={item.createdAt} language={language} /></td>
            <td>{auditActor(item, text)}</td>
            <td>{item.reason || "—"}</td>
            <td>{auditChanges(item.safeChanges, text)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    {entries.length === 0 && (
      <div className="account-audit-empty">{text.noAuditLogs}</div>
    )}
  </div>
);

const AccountDrawer = ({
  type,
  selected,
  show,
  onClose,
  onChanged,
  text,
  direction,
  language
}) => {
  const [detail, setDetail] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [profile, setProfile] = useState({});
  const [revealedNationalId, setRevealedNationalId] = useState("");
  const [nationalIdLoading, setNationalIdLoading] = useState(false);
  const [documentViewer, setDocumentViewer] = useState(null);
  const [documentLoading, setDocumentLoading] = useState("");
  const {
    drawerBounds,
    drawerResizing,
    drawerStyle,
    drawerWidth,
    cancelDrawerResize,
    moveDrawerResize,
    resetDrawerWidth,
    resizeDrawerWithKeyboard,
    startDrawerResize,
    stopDrawerResize
  } = useResizableDrawer(direction);
  const documentUrlRef = useRef("");
  const documentRequestRef = useRef(0);
  const id = selected?._id || selected?.id;
  const customer = type === "customers";
  const endpoint = customer ? "/user" : "/seller";

  const hydrateAccountDetail = useCallback((value) => {
    setDetail(value);
    setProfile(customer
      ? {
          fullName: value?.fullName || "",
          phone: value?.phone || "",
          nationalId: ""
        }
      : {
          nameAr: value?.nameAr || "",
          nameEn: value?.nameEn || "",
          phone: value?.phone || "",
          registrationNumber: value?.registrationNumber || ""
        });
  }, [customer]);

  const refreshAccountDetail = useCallback(() =>
    getData(`${endpoint}/${id}`, true).then((response) => {
      const value = response?.data?.data || null;
      hydrateAccountDetail(value);
      return value;
    }), [endpoint, hydrateAccountDetail, id]);

  useEffect(() => {
    setRevealedNationalId("");
    if (!show || !id) return;
    setLoading(true);
    setDetail(null);
    setSubmissions([]);
    setActiveTab("overview");
    Promise.all([
      getData(`${endpoint}/${id}`, true),
      getData(`/admin/registration-reviews/${type}/${id}/submissions`, true)
        .catch(() => ({ data: { data: [] } }))
    ])
      .then(([detailResponse, submissionResponse]) => {
        const value = detailResponse?.data?.data || null;
        hydrateAccountDetail(value);
        setSubmissions(submissionResponse?.data?.data || []);
      })
      .catch((error) => {
        toast.error(messageFor(error));
        onClose();
      })
      .finally(() => setLoading(false));
  }, [endpoint, hydrateAccountDetail, id, onClose, show, type]);

  useEffect(() => () => {
    documentRequestRef.current += 1;
    if (documentUrlRef.current) URL.revokeObjectURL(documentUrlRef.current);
  }, []);

  const tabs = useMemo(() => [
    ["overview", "fa-user", text.overview],
    ["documents", "fa-folder-open", text.documentsTab],
    ["policies", "fa-shield-halved", text.policiesTab],
    ["operations", "fa-layer-group", text.operationsTab],
    ["audit", "fa-clock-rotate-left", text.logsTab]
  ], [text]);

  const accountName = customer
    ? detail?.fullName
    : language === "ar"
      ? detail?.nameAr || detail?.nameEn
      : detail?.nameEn || detail?.nameAr;
  const reviewStatus = detail?.registrationReview?.status ||
    detail?.registrationReviewStatus;

  const closeDrawer = () => {
    cancelDrawerResize();
    documentRequestRef.current += 1;
    if (documentUrlRef.current) URL.revokeObjectURL(documentUrlRef.current);
    documentUrlRef.current = "";
    setDocumentViewer(null);
    setDocumentLoading("");
    setDialog(null);
    setReason("");
    setRevealedNationalId("");
    onClose();
  };

  const openAction = (action) => {
    setReason("");
    setDialog(action);
  };

  const submitAction = () => {
    if (!dialog || !id) return;
    if (dialog !== "profile" && !reason.trim()) {
      toast.error(text.reasonHint);
      return;
    }
    const path = dialog === "profile" ? "profile" : dialog;
    const changedProfile = Object.fromEntries(
      Object.entries(profile).filter(([, value]) => String(value || "").trim())
    );
    const payload = dialog === "profile"
      ? { ...changedProfile, ...(reason.trim() ? { reason: reason.trim() } : {}) }
      : { reason: reason.trim() };
    setActionLoading(true);
    patchDataToken(`${endpoint}/${id}/${path}`, payload, true)
      .then((response) => {
        toast.success(response?.data?.message || text.confirm);
        setDialog(null);
        onChanged();
        if (["soft-delete", "restore", "reopen-review"].includes(path)) {
          closeDrawer();
          return;
        }
        setDetail((current) => ({ ...current, ...(response?.data?.data || {}) }));
        refreshAccountDetail().catch((error) => toast.error(messageFor(error)));
      })
      .catch((error) => toast.error(messageFor(error)))
      .finally(() => setActionLoading(false));
  };

  const toggleNationalId = () => {
    if (revealedNationalId) {
      setRevealedNationalId("");
      return;
    }
    if (!id || nationalIdLoading) return;
    setNationalIdLoading(true);
    getData(`/admin/registration-reviews/customers/${id}/national-id`, true)
      .then((response) => setRevealedNationalId(response?.data?.data?.nationalId || ""))
      .catch((error) => toast.error(messageFor(error)))
      .finally(() => setNationalIdLoading(false));
  };

  const loadGalleryDocument = useCallback((items, index = 0) => {
    const item = items?.[index];
    if (!id || !item) return Promise.resolve();
    const requestId = documentRequestRef.current + 1;
    documentRequestRef.current = requestId;
    setDocumentLoading(item.key || item.documentType);
    const documentEndpoint = item.kind === "submission"
      ? `/admin/registration-reviews/${type}/${id}/submissions/${item.submissionId}/groups/${item.group}/files/${item.fileId}`
      : `/admin/registration-reviews/${type}/${id}/documents/${item.documentType}`;
    return getBlobData(documentEndpoint, true, language)
      .then((response) => {
        if (documentRequestRef.current !== requestId) return;
        if (documentUrlRef.current) URL.revokeObjectURL(documentUrlRef.current);
        const url = URL.createObjectURL(response.data);
        documentUrlRef.current = url;
        setDocumentViewer({ url, title: item.title, items, index });
      })
      .catch(async (error) =>
        toast.error(await protectedDocumentErrorMessage(error, language))
      )
      .finally(() => {
        if (documentRequestRef.current === requestId) setDocumentLoading("");
      });
  }, [id, language, type]);

  const openProfileImage = () => loadGalleryDocument([{
    key: "profileImage",
    kind: "legacy",
    documentType: "profileImage",
    title: text.profileImage
  }]);

  const navigateDocumentViewer = (delta) => {
    const items = documentViewer?.items || [];
    if (items.length < 2) return;
    const nextIndex = (documentViewer.index + delta + items.length) % items.length;
    loadGalleryDocument(items, nextIndex);
  };

  const closeDocumentViewer = () => {
    documentRequestRef.current += 1;
    if (documentUrlRef.current) URL.revokeObjectURL(documentUrlRef.current);
    documentUrlRef.current = "";
    setDocumentViewer(null);
    setDocumentLoading("");
  };

  const profileFieldLabel = (field) => ({
    fullName: text.name,
    phone: text.phone,
    nationalId: text.nationalId,
    nameAr: `${text.storeName} (AR)`,
    nameEn: `${text.storeName} (EN)`,
    registrationNumber: text.registrationNumber
  })[field] || field;

  return (
    <>
      <Offcanvas
        show={show}
        onHide={closeDrawer}
        placement={direction === "rtl" ? "start" : "end"}
        className="registration-review-drawer account-drawer account-directory-drawer resizable-details-drawer"
        style={drawerStyle}
      >
        <div
          className={`account-drawer-resizer${drawerResizing ? " is-resizing" : ""}`}
          role="separator"
          aria-orientation="vertical"
          aria-label={text.resizeDrawer}
          aria-valuemin={drawerBounds.min}
          aria-valuemax={drawerBounds.max}
          aria-valuenow={drawerWidth}
          tabIndex="0"
          title={text.resizeDrawer}
          onPointerDown={startDrawerResize}
          onPointerMove={moveDrawerResize}
          onPointerUp={stopDrawerResize}
          onPointerCancel={stopDrawerResize}
          onLostPointerCapture={stopDrawerResize}
          onDoubleClick={resetDrawerWidth}
          onKeyDown={resizeDrawerWithKeyboard}
        >
          <span aria-hidden="true" />
        </div>
        <Offcanvas.Header closeButton>
          <div>
            <p className="registration-review-drawer-eyebrow">
              {customer ? text.customerAccount : text.merchantAccount}
            </p>
            <Offcanvas.Title>{text.accountDetails}</Offcanvas.Title>
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body dir={direction}>
          {loading || !detail ? (
            <div className="registration-review-drawer-loading">
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
              {text.loading}
            </div>
          ) : (
            <>
              <section className="registration-review-summary account-directory-summary">
                <button
                  type="button"
                  className="registration-review-avatar account-directory-profile-button"
                  disabled={
                    !(detail.profileImageAvailable || detail.image || detail.logo) ||
                    documentLoading === "profileImage"
                  }
                  aria-label={text.openProfileImage}
                  onClick={openProfileImage}
                >
                  <ProtectedProfileImage
                    type={type}
                    accountId={id}
                    available={Boolean(
                      detail.profileImageAvailable || detail.image || detail.logo
                    )}
                    name={accountName}
                    imageLabel={text.profileImage}
                    className="registration-review-avatar-image account-directory-avatar"
                  />
                </button>
                <div>
                  <h2>{accountName || "—"}</h2>
                  <p>{detail.email}</p>
                  {!customer && detail.ownerFullLegalName && (
                    <small>{detail.ownerFullLegalName}</small>
                  )}
                </div>
                <div className="account-directory-summary-statuses">
                  <AccountStatusBadge
                    status={detail.lifecycleState}
                    label={statusLabel(detail.lifecycleState, text)}
                  />
                </div>
              </section>

              <section className="registration-review-section account-directory-actions-section">
                <SectionTitle
                  icon="fa-wand-magic-sparkles"
                  title={text.accountActions}
                  hint={text.accountActionsHint}
                />
                <div className="account-drawer-actions">
                  <button type="button" onClick={() => openAction("profile")}>
                    <i className="fa-solid fa-pen" aria-hidden="true" />{text.editProfile}
                  </button>
                  {detail.isBlocked ? (
                    <button type="button" onClick={() => openAction("unblock")}>
                      <i className="fa-solid fa-unlock" aria-hidden="true" />{text.unblock}
                    </button>
                  ) : (
                    <button type="button" onClick={() => openAction("block")}>
                      <i className="fa-solid fa-lock" aria-hidden="true" />{text.block}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={reviewStatus === "PENDING_REVIEW"}
                    className={reviewStatus === "PENDING_REVIEW" ? "is-complete" : ""}
                    onClick={() => openAction("reopen-review")}
                  >
                    <i
                      className={`fa-solid ${reviewStatus === "PENDING_REVIEW" ? "fa-clock" : "fa-rotate-left"}`}
                      aria-hidden="true"
                    />
                    {reviewStatus === "PENDING_REVIEW" ? text.pending : text.reopenReview}
                  </button>
                  {detail.lifecycleState === "DELETED" ? (
                    <button type="button" onClick={() => openAction("restore")}>
                      <i className="fa-solid fa-trash-arrow-up" aria-hidden="true" />{text.restore}
                    </button>
                  ) : (
                    <button type="button" className="is-danger" onClick={() => openAction("soft-delete")}>
                      <i className="fa-solid fa-trash" aria-hidden="true" />{text.softDelete}
                    </button>
                  )}
                </div>
              </section>

              <div className="account-drawer-tabs" role="tablist">
                {tabs.map(([key, icon, label]) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === key}
                    className={activeTab === key ? "is-active" : ""}
                    onClick={() => setActiveTab(key)}
                  >
                    <i className={`fa-solid ${icon}`} aria-hidden="true" />
                    {label}
                  </button>
                ))}
              </div>

              {activeTab === "overview" && (
                <section className="registration-review-section">
                  <SectionTitle
                    icon="fa-user"
                    title={text.overview}
                    hint={text.accountOverviewHint}
                  />
                  <RegistrationAccountOverview
                    type={type}
                    account={detail}
                    text={text}
                    language={language}
                    revealedNationalId={revealedNationalId}
                    nationalIdLoading={nationalIdLoading}
                    onToggleNationalId={toggleNationalId}
                  />
                  {!customer && (
                    <div className="account-directory-completeness-card">
                      <div className="account-directory-completeness-heading">
                        <span>{text.setupStatus}</span>
                        <strong>{text.merchantSetupProgress}</strong>
                      </div>
                      <MerchantProfileCompleteness
                        merchant={detail}
                        text={text}
                        language={language}
                        details
                      />
                    </div>
                  )}
                </section>
              )}

              {activeTab === "documents" && (
                <section className="registration-review-section">
                  <SectionTitle
                    icon="fa-folder-open"
                    title={text.registrationDocuments}
                    hint={text.protectedDocumentsHint}
                  />
                  <RegistrationSubmissionHistory
                    submissions={submissions}
                    currentSubmissionId={submissions[0]?.id}
                    language={language}
                    text={text}
                    loading={documentLoading}
                    onViewGallery={loadGalleryDocument}
                  />
                </section>
              )}

              {activeTab === "policies" && (
                <section className="registration-review-section">
                  <SectionTitle
                    icon="fa-shield-halved"
                    title={text.policyEvidence}
                    hint={text.policyEvidenceHint}
                  />
                  <PolicyEvidenceList
                    entries={detail.policyEvidence}
                    text={text}
                    language={language}
                  />
                </section>
              )}

              {activeTab === "operations" && (
                <section className="registration-review-section">
                  <SectionTitle icon="fa-layer-group" title={text.operationsSummary} />
                  <DetailGrid entries={[
                    [text.total, detail.operationsSummary?.total],
                    ...Object.entries(detail.operationsSummary?.byStatus || {})
                  ]} />
                </section>
              )}

              {activeTab === "audit" && (
                <section className="registration-review-section">
                  <SectionTitle icon="fa-clock-rotate-left" title={text.auditHistory} />
                  <AccountAuditLogTable
                    entries={detail.auditHistory || []}
                    text={text}
                    language={language}
                  />
                </section>
              )}
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      <Modal show={Boolean(dialog)} onHide={() => setDialog(null)} centered dir={direction}>
        <Modal.Header closeButton>
          <Modal.Title>{dialog === "profile" ? text.editProfile : text.confirm}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {dialog === "profile" && Object.entries(profile).map(([field, value]) => (
            <label className="account-modal-field" key={field}>
              <span>{profileFieldLabel(field)}</span>
              <input
                value={value}
                onChange={(event) => setProfile((current) => ({
                  ...current,
                  [field]: event.target.value
                }))}
              />
            </label>
          ))}
          <label className="account-modal-field">
            <span>{text.reason}</span>
            <textarea
              value={reason}
              placeholder={text.reasonHint}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="account-modal-cancel" onClick={() => setDialog(null)}>
            {text.cancel}
          </button>
          <button
            type="button"
            className="account-modal-confirm"
            disabled={actionLoading}
            onClick={submitAction}
          >
            {actionLoading ? text.loading : dialog === "profile" ? text.save : text.confirm}
          </button>
        </Modal.Footer>
      </Modal>

      <SecureDocumentViewer
        url={documentViewer?.url}
        title={documentViewer?.title}
        index={documentViewer?.index || 0}
        total={documentViewer?.items?.length || 1}
        onPrevious={() => navigateDocumentViewer(-1)}
        onNext={() => navigateDocumentViewer(1)}
        onClose={closeDocumentViewer}
        loading={Boolean(documentLoading)}
        text={text}
      />
    </>
  );
};

export default AccountDrawer;
